import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Star, Users, BookOpen, Video, Sparkles, CheckCircle, Play, Calendar, ChevronDown, Clock, TrendingUp, Shield, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import GeometricAvatar from '../components/GeometricAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Landing() {
  const [tutor, setTutor] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadEmail, setLeadEmail] = useState('');
  const [leadName, setLeadName] = useState('');
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);

  // Calculate next Monday for urgency
  const getNextMonday = () => {
    const now = new Date();
    const day = now.getDay();
    const daysUntilMonday = day === 0 ? 1 : (8 - day);
    const next = new Date(now);
    next.setDate(now.getDate() + daysUntilMonday);
    next.setHours(8, 0, 0, 0);
    const diff = next - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    return { days, hours };
  };
  const countdown = getNextMonday();

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadEmail.trim()) return;
    setLeadLoading(true);
    try {
      await axios.post(`${API_URL}/api/leads`, { email: leadEmail, name: leadName });
      setLeadSubmitted(true);
    } catch {
      setLeadSubmitted(true);
    } finally {
      setLeadLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tutorRes, coursesRes] = await Promise.all([
          axios.get(`${API_URL}/api/tutor`),
          axios.get(`${API_URL}/api/courses`)
        ]);
        setTutor(tutorRes.data);
        setCourses(coursesRes.data.slice(0, 3));
      } catch (error) {
        // Show static fallback content if API fails
        setTutor({
          name: "Ace Black",
          bio: "Founder and CEO of RikirTutor with over 15 years of experience in education. Ace is certified in WorkKeys, GED preparation, and SAT preparation. His deep academic expertise and passion for helping students achieve their goals has led to a 95% pass rate among his students.",
          certifications: ["WorkKeys Certified Instructor", "GED Testing Service Certified", "College Board SAT Preparation Certified", "National Tutoring Association Certified"],
          rating: 4.9,
          total_reviews: 328,
          experience_years: 15
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="bg-white" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[#F9FAFB]"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
              <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#002FA7] mb-4">
                TUTORING FROM $15/CLASS
              </span>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-none text-[#0A0A0A] mb-6">
                Pass Your GED.<br />
                Raise Your SAT.<br />
                <span className="text-[#002FA7]">Succeed in College.</span>
              </h1>
              <p className="text-lg text-[#4B5563] leading-relaxed max-w-xl mb-3">
                GED, SAT, WorkKeys preparation and college courses — all from one expert tutor. 
                Single classes from $15, bundles, weekly, and unlimited monthly plans available.
              </p>
              <p className="text-sm font-bold text-[#E53935] mb-8 flex items-center gap-2">
                <span className="w-2 h-2 bg-[#E53935] rounded-full animate-pulse" />
                Limited spots available each week
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/pricing">
                  <Button 
                    className="bg-[#002FA7] hover:bg-[#001B63] text-white font-bold px-8 py-6 rounded-sm text-base"
                    data-testid="hero-subscribe"
                  >
                    Book Your First Session
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button 
                    variant="outline" 
                    className="border-[#0A0A0A] text-[#0A0A0A] font-bold px-8 py-6 rounded-sm text-base"
                    data-testid="hero-learn-more"
                    onClick={(e) => {
                      const section = document.querySelector('[data-testid="tutor-section"]');
                      if (section) {
                        e.preventDefault();
                        section.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    Meet Your Tutor
                  </Button>
                </Link>
              </div>
              {/* Stats */}
              <div className="flex gap-12 mt-12 pt-8 border-t border-[#E5E7EB]">
                <div>
                  <p className="text-3xl font-black text-[#0A0A0A]">500+</p>
                  <p className="text-sm text-[#4B5563]">Students Tutored</p>
                </div>
                <div>
                  <p className="text-3xl font-black text-[#0A0A0A]">95%</p>
                  <p className="text-sm text-[#4B5563]">Pass Rate</p>
                </div>
                <div>
                  <p className="text-3xl font-black text-[#0A0A0A]">4.9</p>
                  <p className="text-sm text-[#4B5563]">Average Rating</p>
                </div>
              </div>
            </div>
            <div className="md:col-span-5 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
              <div className="relative">
                <div className="w-full aspect-square max-w-md mx-auto rounded-sm overflow-hidden shadow-2xl">
                  <GeometricAvatar size={400} className="w-full h-full" />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white p-4 shadow-lg rounded-sm border border-[#E5E7EB]">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#002FA7]/10 rounded-full flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-[#002FA7]" />
                    </div>
                    <div>
                      <p className="font-bold text-[#0A0A0A]">Flexible Plans</p>
                      <p className="text-sm text-[#4B5563]">From $15/class</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Your Tutor Section */}
      <section className="py-20 md:py-32 px-6 bg-[#0A0A0A]" data-testid="tutor-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FFC107] mb-4 block">
                YOUR INSTRUCTOR
              </span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">
                Meet Ace Black
              </h2>
              {loading ? (
                <div className="animate-pulse">
                  <div className="h-4 bg-white/20 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-white/20 rounded w-full mb-4"></div>
                  <div className="h-4 bg-white/20 rounded w-2/3"></div>
                </div>
              ) : tutor && (
                <>
                  <p className="text-white/80 leading-relaxed mb-6">{tutor.bio}</p>
                  <div className="flex items-center gap-2 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-5 h-5 ${i < Math.floor(tutor.rating) ? 'text-[#FFC107] fill-[#FFC107]' : 'text-white/30'}`} 
                      />
                    ))}
                    <span className="text-white/60 ml-2">{tutor.rating} ({tutor.total_reviews} reviews)</span>
                  </div>
                  <div className="space-y-3 mb-8">
                    <h4 className="font-bold text-white">Certifications:</h4>
                    <div className="flex flex-wrap gap-2">
                      {tutor.certifications?.map((cert, i) => (
                        <span key={i} className="text-xs bg-white/10 text-white px-3 py-1 rounded-sm">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="order-1 md:order-2">
              <div className="w-full max-w-sm mx-auto">
                <GeometricAvatar size={350} className="w-full rounded-sm shadow-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 md:py-32 px-6" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#002FA7] mb-4 block">
              WHAT'S INCLUDED
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0A0A0A]">
              Everything You Need to Succeed
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card tutor-card group" data-testid="feature-sessions">
              <div className="w-14 h-14 bg-[#002FA7]/10 rounded-sm flex items-center justify-center mb-6 group-hover:bg-[#002FA7] transition-colors">
                <Calendar className="w-7 h-7 text-[#002FA7] group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-[#0A0A0A] mb-3">Unlimited Live Sessions</h3>
              <p className="text-[#4B5563] mb-4">
                Book as many one-on-one tutoring sessions as you need. Priority scheduling ensures you get the times that work for you.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-[#4B5563]">
                  <CheckCircle className="w-4 h-4 text-[#002FA7]" />
                  Priority booking access
                </li>
                <li className="flex items-center gap-2 text-sm text-[#4B5563]">
                  <CheckCircle className="w-4 h-4 text-[#002FA7]" />
                  24-hour email reminders
                </li>
              </ul>
            </div>

            <div className="card tutor-card group" data-testid="feature-videos">
              <div className="w-14 h-14 bg-[#E53935]/10 rounded-sm flex items-center justify-center mb-6 group-hover:bg-[#E53935] transition-colors">
                <Play className="w-7 h-7 text-[#E53935] group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-[#0A0A0A] mb-3">Video Library Access</h3>
              <p className="text-[#4B5563] mb-4">
                Access our complete library of pre-recorded lessons. Learn at your own pace, anytime, anywhere.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-[#4B5563]">
                  <CheckCircle className="w-4 h-4 text-[#E53935]" />
                  GED, SAT & WorkKeys content
                </li>
                <li className="flex items-center gap-2 text-sm text-[#4B5563]">
                  <CheckCircle className="w-4 h-4 text-[#E53935]" />
                  College course materials
                </li>
              </ul>
            </div>

            <div className="card tutor-card group" data-testid="feature-ai">
              <div className="w-14 h-14 bg-[#FFC107]/20 rounded-sm flex items-center justify-center mb-6 group-hover:bg-[#FFC107] transition-colors">
                <Sparkles className="w-7 h-7 text-[#0A0A0A]" />
              </div>
              <h3 className="text-xl font-bold text-[#0A0A0A] mb-3">AI Study Tools</h3>
              <p className="text-[#4B5563] mb-4">
                24/7 AI-powered study assistant and practice question generator to help you prepare anytime.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-[#4B5563]">
                  <CheckCircle className="w-4 h-4 text-[#0A0A0A]" />
                  AI chatbot assistance
                </li>
                <li className="flex items-center gap-2 text-sm text-[#4B5563]">
                  <CheckCircle className="w-4 h-4 text-[#0A0A0A]" />
                  Custom practice tests
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 md:py-28 px-6 bg-[#F9FAFB]" data-testid="how-it-works">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#002FA7] mb-4 block">
              SIMPLE PROCESS
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0A0A0A]">
              How It Works
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '1', icon: <Calendar className="w-6 h-6" />, title: 'Book Your Session', desc: 'Pick a date, time, and subject that works for you.' },
              { step: '2', icon: <Video className="w-6 h-6" />, title: 'Join Live Class', desc: 'Connect via video call with your tutor, Ace Black.' },
              { step: '3', icon: <Users className="w-6 h-6" />, title: 'Get Personalized Help', desc: '1-on-1 attention on exactly what you need to improve.' },
              { step: '4', icon: <TrendingUp className="w-6 h-6" />, title: 'Track Your Progress', desc: 'Watch your scores improve with quizzes and progress tracking.' },
            ].map((item) => (
              <div key={item.step} className="text-center" data-testid={`step-${item.step}`}>
                <div className="w-16 h-16 bg-[#002FA7] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-black">
                  {item.step}
                </div>
                <h3 className="font-bold text-[#0A0A0A] mb-2">{item.title}</h3>
                <p className="text-sm text-[#4B5563]">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/pricing">
              <Button className="bg-[#002FA7] hover:bg-[#001B63] text-white font-bold px-8 py-6 rounded-sm text-base" data-testid="hiw-cta">
                Start Learning Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Urgency Banner */}
      <section className="py-6 px-6 bg-[#E53935]" data-testid="urgency-banner">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-white text-center">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            <span className="font-bold">Only 6 spots left this week</span>
          </div>
          <div className="hidden sm:block w-px h-6 bg-white/30" />
          <div className="flex items-center gap-2">
            <span className="text-sm">Next session starts Monday in</span>
            <span className="font-black text-lg">{countdown.days}d {countdown.hours}h</span>
          </div>
          <Link to="/pricing">
            <Button className="bg-white text-[#E53935] hover:bg-white/90 font-bold rounded-sm px-6" data-testid="urgency-cta">
              Grab Your Spot
            </Button>
          </Link>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 md:py-28 px-6" data-testid="testimonials-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#002FA7] mb-4 block">
              STUDENT SUCCESS
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0A0A0A]">
              Real Results from Real Students
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'Marcus J.',
                program: 'GED',
                quote: 'I passed my GED in just 3 weeks with Ace. He broke everything down so it actually made sense. I went from failing practice tests to scoring in the 90th percentile.',
                result: 'Passed GED in 3 weeks',
                initials: 'MJ',
                color: '#002FA7'
              },
              {
                name: 'Aaliyah T.',
                program: 'SAT',
                quote: 'My SAT score jumped 240 points after 2 months of tutoring. Ace taught me strategies I never learned in school. The AI practice tests were a game changer.',
                result: '+240 SAT points',
                initials: 'AT',
                color: '#E53935'
              },
              {
                name: 'David R.',
                program: 'College',
                quote: 'I was failing College Algebra and about to drop the class. After 4 sessions with Ace, I ended the semester with a B+. The video library helped me study between sessions.',
                result: 'F to B+ in College Algebra',
                initials: 'DR',
                color: '#16A34A'
              },
            ].map((t, i) => (
              <div key={i} className="bg-white border border-[#E5E7EB] p-6 rounded-sm" data-testid={`testimonial-${i}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: t.color }}>
                    {t.initials}
                  </div>
                  <div>
                    <p className="font-bold text-[#0A0A0A]">{t.name}</p>
                    <p className="text-xs text-[#4B5563]">{t.program} Student</p>
                  </div>
                </div>
                <div className="flex mb-3">
                  {[...Array(5)].map((_, si) => (
                    <Star key={si} className="w-4 h-4 text-[#FFC107] fill-[#FFC107]" />
                  ))}
                </div>
                <p className="text-sm text-[#4B5563] mb-4 leading-relaxed">"{t.quote}"</p>
                <div className="pt-3 border-t border-[#E5E7EB]">
                  <span className="text-xs font-bold text-[#059669] flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> {t.result}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free Study Plan Lead Capture */}
      <section className="py-20 md:py-28 px-6 bg-[#0A0A0A]" data-testid="lead-capture-section">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 bg-[#FFC107]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-[#FFC107]" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
            Get Your Free Study Plan
          </h2>
          <p className="text-white/60 mb-2">
            Not ready to commit? No problem. Get a personalized study plan for GED, SAT, or college courses — completely free.
          </p>
          <p className="text-white/40 text-sm mb-8">
            Join 500+ students who started here.
          </p>
          {!leadSubmitted ? (
            <form onSubmit={handleLeadSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto" data-testid="lead-form">
              <Input
                type="text"
                placeholder="Your name"
                value={leadName}
                onChange={(e) => setLeadName(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-sm"
                data-testid="lead-name"
              />
              <Input
                type="email"
                placeholder="Your email"
                value={leadEmail}
                onChange={(e) => setLeadEmail(e.target.value)}
                required
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-sm"
                data-testid="lead-email"
              />
              <Button
                type="submit"
                disabled={leadLoading}
                className="bg-[#FFC107] hover:bg-[#FFD54F] text-[#0A0A0A] font-bold rounded-sm whitespace-nowrap px-6"
                data-testid="lead-submit"
              >
                {leadLoading ? 'Sending...' : 'Get Free Plan'}
              </Button>
            </form>
          ) : (
            <div className="bg-[#059669]/20 border border-[#059669]/30 rounded-sm p-6 max-w-lg mx-auto" data-testid="lead-success">
              <CheckCircle className="w-8 h-8 text-[#059669] mx-auto mb-3" />
              <p className="text-white font-bold">You're in!</p>
              <p className="text-white/60 text-sm">Your free study plan is on its way. Check your email shortly.</p>
            </div>
          )}
          <div className="flex items-center justify-center gap-6 mt-8 text-white/40 text-xs">
            <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> No spam, ever</span>
            <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> 100% free</span>
          </div>
        </div>
      </section>

      {/* Sample Lessons / Teaser Videos */}
      <section className="py-20 md:py-32 px-6 bg-[#0A0A0A]" data-testid="teaser-videos-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FFC107] mb-4 block">
              FREE PREVIEWS
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
              Sample Our Lessons
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Watch a quick preview from each program. Full lessons and our complete video library are available when you subscribe.
            </p>
          </div>

          {/* GED Samples */}
          <div className="mb-14">
            <div className="flex items-center gap-3 mb-6">
              <span className="bg-[#002FA7] text-white px-3 py-1 text-xs font-bold tracking-wider">GED</span>
              <h3 className="text-lg font-bold text-white">GED Preparation Samples</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { id: 'D5iuMePlpLI', title: 'Math: GED Math Essentials', subject: 'Math' },
                { id: '4-8DHjO6ClE', title: 'Science: GED Science Basics', subject: 'Science' },
                { id: 'Ki2565zYc48', title: 'English: Must-Know Grammar Lessons', subject: 'Language Arts' },
              ].map((video) => (
                <div key={video.id} className="group" data-testid={`teaser-ged-${video.subject.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="relative aspect-video rounded-sm overflow-hidden mb-3 border border-white/10">
                    <iframe
                      src={`https://www.youtube.com/embed/${video.id}?start=0&end=60&rel=0&modestbranding=1`}
                      title={video.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <p className="text-white font-medium text-sm">{video.title}</p>
                  <p className="text-white/40 text-xs mt-1">~1 min preview</p>
                </div>
              ))}
            </div>
          </div>

          {/* SAT Samples */}
          <div className="mb-14">
            <div className="flex items-center gap-3 mb-6">
              <span className="bg-[#E53935] text-white px-3 py-1 text-xs font-bold tracking-wider">SAT</span>
              <h3 className="text-lg font-bold text-white">SAT Preparation Samples</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { id: 'DHvyNjcDBYE', title: 'Math: SAT Math Tips & Strategies', subject: 'Math' },
                { id: 'Q4ylJ4HxgbE', title: 'Reading: Strategies to Score 700+', subject: 'Reading' },
                { id: '8cxBQnzCyUM', title: 'Writing: Every Grammar Rule You Need', subject: 'Writing' },
              ].map((video) => (
                <div key={video.id} className="group" data-testid={`teaser-sat-${video.subject.toLowerCase()}`}>
                  <div className="relative aspect-video rounded-sm overflow-hidden mb-3 border border-white/10">
                    <iframe
                      src={`https://www.youtube.com/embed/${video.id}?start=0&end=60&rel=0&modestbranding=1`}
                      title={video.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <p className="text-white font-medium text-sm">{video.title}</p>
                  <p className="text-white/40 text-xs mt-1">~1 min preview</p>
                </div>
              ))}
            </div>
          </div>

          {/* College Samples */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="bg-[#FFC107] text-[#0A0A0A] px-3 py-1 text-xs font-bold tracking-wider">COLLEGE</span>
              <h3 className="text-lg font-bold text-white">College Course Samples</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { id: 'fQ3Z5XmvtnE', title: 'Math: College Algebra Introduction', subject: 'Math' },
                { id: 'URUJD5NEXC8', title: 'Science: Cell Structure & Biology', subject: 'Science' },
                { id: 'MFCjQAjpYcg', title: 'Business: Introduction to Economics', subject: 'Business' },
              ].map((video) => (
                <div key={video.id} className="group" data-testid={`teaser-college-${video.subject.toLowerCase()}`}>
                  <div className="relative aspect-video rounded-sm overflow-hidden mb-3 border border-white/10">
                    <iframe
                      src={`https://www.youtube.com/embed/${video.id}?start=0&end=60&rel=0&modestbranding=1`}
                      title={video.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <p className="text-white font-medium text-sm">{video.title}</p>
                  <p className="text-white/40 text-xs mt-1">~1 min preview</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-14">
            <p className="text-white/60 mb-4">Want the full lessons? Subscribe and get unlimited access.</p>
            <Link to="/pricing">
              <Button className="bg-[#002FA7] hover:bg-[#001B63] text-white font-bold px-8 py-6 rounded-sm text-base" data-testid="teaser-cta">
                Book Your First Session
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-20 md:py-32 px-6 bg-[#F9FAFB]" data-testid="programs-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#002FA7] mb-4 block">
              PROGRAMS COVERED
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0A0A0A]">
              Comprehensive Preparation
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 border border-[#E5E7EB]">
              <span className="badge-ged inline-block mb-3">GED</span>
              <h3 className="font-bold text-[#0A0A0A] mb-2">GED Preparation</h3>
              <p className="text-sm text-[#4B5563]">Math, Science, Social Studies, Language Arts</p>
            </div>
            <div className="bg-white p-6 border border-[#E5E7EB]">
              <span className="badge-sat inline-block mb-3">SAT</span>
              <h3 className="font-bold text-[#0A0A0A] mb-2">SAT Preparation</h3>
              <p className="text-sm text-[#4B5563]">Math, Reading, Writing sections</p>
            </div>
            <div className="bg-white p-6 border border-[#E5E7EB]">
              <span className="bg-[#002FA7]/10 text-[#002FA7] px-3 py-1 text-xs font-bold inline-block mb-3">WORKKEYS</span>
              <h3 className="font-bold text-[#0A0A0A] mb-2">WorkKeys Certification</h3>
              <p className="text-sm text-[#4B5563]">Applied Math, Documents, Business Writing</p>
            </div>
            <div className="bg-white p-6 border border-[#E5E7EB]">
              <span className="badge-college inline-block mb-3">COLLEGE</span>
              <h3 className="font-bold text-[#0A0A0A] mb-2">College Courses</h3>
              <p className="text-sm text-[#4B5563]">Algebra, Chemistry, Biology & more</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28 px-6 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#002FA7] mb-4 block">FAQ</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0A0A0A]">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            <FaqItem
              question="What pricing plans do you offer?"
              answer="We offer flexible plans: Single Class ($15), Group Session ($20), 5-Day Bundle ($150 for 5 sessions), Weekly Package ($100/week includes 3 sessions + group access + video library + AI tools), and Monthly Unlimited ($500/month for everything)."
            />
            <FaqItem
              question="What subjects do you cover?"
              answer="We cover GED preparation (Math, Science, English, Social Studies), SAT preparation (Math, Reading, Writing & Language), College courses (Math, Science, Business, Writing), and WorkKeys certification."
            />
            <FaqItem
              question="How do live sessions work?"
              answer="After subscribing, you can book sessions through your dashboard calendar. Choose a date, time slot, and subject. You'll receive an email confirmation with a meeting link. Sessions are held via video call, and you'll get a reminder 24 hours before."
            />
            <FaqItem
              question="Can I cancel my subscription?"
              answer="Yes, you can cancel anytime. Your access continues until the end of your current billing period."
            />
            <FaqItem
              question="How is the AI study assistant different from the video lessons?"
              answer="The video lessons provide structured curriculum content. The AI study assistant is a chatbot you can ask any question to — it provides instant, personalized explanations. The practice question generator creates custom problems tailored to your subjects."
            />
            <FaqItem
              question="Do I need any prior knowledge to start?"
              answer="No! Our curriculum starts from the basics. Module 1 in each subject covers fundamentals, and you progress at your own pace through more advanced modules."
            />
            <FaqItem
              question="How quickly can I expect to see results?"
              answer="Most students see improvement within 2-4 weeks of consistent study. Our progress tracker helps you monitor quiz scores and completion across all modules."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 px-6 bg-[#002FA7]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">
            Ready to Transform Your Future?
          </h2>
          <p className="text-white/80 text-lg mb-3">
            Join hundreds of students who have achieved their academic goals with RikirTutor.
          </p>
          <p className="text-[#FFC107] text-sm font-bold mb-2 tracking-wide">
            If you don't improve, your next session is free.
          </p>
          <p className="text-white text-2xl font-bold mb-4">
            Plans from $15/class • Monthly from $500
          </p>
          <p className="text-white/50 text-xs mb-8 flex items-center justify-center gap-2">
            <span className="w-1.5 h-1.5 bg-[#FFC107] rounded-full animate-pulse" />
            Limited spots available each week
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/pricing">
              <Button 
                className="bg-white text-[#002FA7] hover:bg-[#F9FAFB] font-bold px-8 py-6 rounded-sm text-base"
                data-testid="cta-subscribe"
              >
                Start Learning Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/register">
              <Button 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 font-bold px-8 py-6 rounded-sm text-base"
                data-testid="cta-register"
              >
                Create Free Account
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#E5E7EB] rounded-sm" data-testid="faq-item">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left"
      >
        <span className="font-medium text-[#0A0A0A] pr-4">{question}</span>
        <ChevronDown className={`w-5 h-5 text-[#4B5563] flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-[#4B5563] leading-relaxed border-t border-[#E5E7EB] pt-4">
          {answer}
        </div>
      )}
    </div>
  );
}
